import { useEffect, useState } from 'react';
import { MapPin, RotateCcw, Search } from 'lucide-react';
import { searchTerritories } from '../../api/endpoints';
import { useFilters } from '../../app/FiltersContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';

const fuels = [
  ['BENZINA', 'Benzina'],
  ['GASOLIO', 'Diesel'],
  ['GPL', 'GPL'],
  ['METANO', 'Metano'],
];

export default function QuickFuelControls({ showTerritory = true, compact = false, horizontal = false }) {
  const { fuel, setFuel, service, setService, territory, setTerritory } = useFilters();
  const [q, setQ] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!showTerritory) return;
    const timer = setTimeout(() => {
      if (q.trim().length < 2) return setOptions([]);
      searchTerritories(q).then(setOptions).catch(() => setOptions([]));
    }, 220);
    return () => clearTimeout(timer);
  }, [q, showTerritory]);

  return (
    <div className={horizontal ? 'grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,.8fr)_minmax(0,1.35fr)] lg:items-end' : compact ? 'min-w-0 space-y-3' : 'min-w-0 space-y-5'}>
      <div>
        {!compact && <div className="mb-2.5 text-xs font-medium text-md-on-surface-variant">Carburante</div>}
        <div className="no-scrollbar max-w-full overflow-x-auto pb-1">
          <ToggleGroup
            type="single"
            value={fuel}
            onValueChange={(value) => value && setFuel(value)}
            className={horizontal ? 'grid w-full grid-cols-4 gap-1' : 'w-max justify-start gap-2'}
          >
            {fuels.map(([value, label]) => (
              <ToggleGroupItem
                key={value}
                value={value}
                className={`${horizontal ? 'min-w-0 px-2 text-xs sm:text-sm' : 'shrink-0 px-5'} h-11 border-0 bg-md-surface-container text-md-on-surface-variant hover:bg-md-primary-container hover:text-md-on-primary-container data-[state=on]:bg-md-primary data-[state=on]:text-md-on-primary`}
              >
                {label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <div>
        {!compact && <div className="mb-2.5 text-xs font-medium text-md-on-surface-variant">Modalità di rifornimento</div>}
        <Tabs value={service} onValueChange={setService} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-md-surface-container text-md-on-surface-variant">
            <TabsTrigger value="self" className="data-[state=active]:bg-md-secondary-container data-[state=active]:text-md-on-secondary-container data-[state=active]:shadow-none">Self-service</TabsTrigger>
            <TabsTrigger value="served" className="data-[state=active]:bg-md-secondary-container data-[state=active]:text-md-on-secondary-container data-[state=active]:shadow-none">Servito</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {showTerritory && (
        <div className="relative">
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 text-xs font-medium text-md-on-surface-variant">
              <MapPin className="h-4 w-4 shrink-0 text-md-secondary" />
              <span className="truncate">Area: <b className="font-bold text-md-on-surface">{territory.name}</b></span>
            </div>
            {territory.type !== 'country' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-md-primary hover:bg-md-primary-container hover:text-md-on-primary-container"
                onClick={() => setTerritory({ type: 'country', code: 'IT', name: 'Italia' })}
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Italia
              </Button>
            )}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-[19px] z-10 h-4 w-4 text-md-on-surface-variant" />
            <Input
              className="filled-field pl-11 text-md-on-surface placeholder:text-md-on-surface-variant"
              value={q}
              onFocus={() => setOpen(true)}
              onChange={e => { setQ(e.target.value); setOpen(true); }}
              placeholder="Cerca regione, provincia o comune"
            />
          </div>
          {open && options.length > 0 && (
            <div className="absolute z-50 mt-2 max-h-72 w-full overflow-auto rounded-[24px] bg-md-surface-container-low p-2 shadow-md2">
              {options.slice(0, 10).map(o => (
                <button
                  key={`${o.type}-${o.code}`}
                  type="button"
                  className="md-press flex min-h-12 w-full items-center gap-3 rounded-full px-4 text-left text-sm text-md-on-surface hover:bg-md-primary-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-md-primary"
                  onClick={() => { setTerritory(o); setQ(''); setOptions([]); setOpen(false); }}
                >
                  <MapPin className="h-4 w-4 text-md-secondary" />
                  <span className="min-w-0 flex-1 truncate font-medium">{o.name}</span>
                  <span className="text-[10px] uppercase tracking-[0.08em] text-md-on-surface-variant">{o.type}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
